import { useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceDot } from "recharts";
import { SIGNAL_RUNS } from "../data/signals";
import { ChartPanel, tooltipStyle, axisProps } from "./common";

const TONE = { success: "text-success border-success/40 bg-success/10", warn: "text-warn border-warn/40 bg-warn/10", critical: "text-critical border-critical/40 bg-critical/10" };

export default function SignalViewer() {
  const [runId, setRunId] = useState("A");
  const run = SIGNAL_RUNS.find((r) => r.id === runId);

  return (
    <ChartPanel
      title="Mock Signal Viewer — Analytical Run Files"
      subtitle="synthetic electropherogram-style traces with automated feature overlay"
      testId="signal-viewer"
      action={
        <span className={`font-mono text-[10px] px-2 py-1 rounded-sm border ${TONE[run.verdictTone]}`} data-testid="signal-verdict">
          {run.verdict}
        </span>
      }
    >
      <div className="flex flex-wrap gap-2 mb-3">
        {SIGNAL_RUNS.map((r) => (
          <button
            key={r.id}
            data-testid={`signal-run-${r.id}`}
            onClick={() => setRunId(r.id)}
            className={`font-mono text-[11px] px-3 py-1.5 rounded-sm border transition-colors ${
              runId === r.id ? "bg-cyan text-navy border-cyan" : "border-teal/40 text-pale/70 hover:border-cyan/60 hover:text-paper"
            }`}
          >
            {r.name}
          </button>
        ))}
        <span className="font-mono text-[10px] text-steel self-center ml-1">{run.desc}</span>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={run.points} margin={{ left: -18, right: 12, top: 8 }}>
          <CartesianGrid stroke="rgba(32,184,205,0.08)" vertical={false} />
          <XAxis dataKey="t" {...axisProps} label={{ value: "Migration time (min, synthetic)", position: "insideBottom", offset: -2, fill: "#8A99A8", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
          <YAxis {...axisProps} label={{ value: "Signal (synthetic)", angle: -90, position: "insideLeft", fill: "#8A99A8", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
          <Tooltip {...tooltipStyle} formatter={(v) => [v, "Signal"]} labelFormatter={(v) => `${v} min`} />
          <ReferenceLine y={run.threshold} stroke="#FFB347" strokeDasharray="4 4" label={{ value: "Detection threshold", fill: "#FFB347", fontSize: 9, fontFamily: "IBM Plex Mono", position: "insideTopRight" }} />
          <ReferenceLine x={run.referenceMarker} stroke="#8A99A8" strokeDasharray="3 3" label={{ value: "Expected marker", fill: "#8A99A8", fontSize: 9, fontFamily: "IBM Plex Mono", position: "insideTopLeft" }} />
          <ReferenceLine y={run.noiseBaseline} stroke="#137C8B" strokeDasharray="2 4" label={{ value: "Noise baseline", fill: "#137C8B", fontSize: 9, fontFamily: "IBM Plex Mono", position: "insideBottomRight" }} />
          <Line type="monotone" dataKey="signal" stroke="#20B8CD" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Signal" />
          {run.peaks.map((p) => (
            <ReferenceDot key={p.label} x={p.t} y={p.h + run.noiseBaseline} r={4} fill="#34C759" stroke="#07111F" />
          ))}
          {run.artifact && <ReferenceDot x={run.artifact.t} y={run.artifact.h + run.noiseBaseline} r={5} fill="#FF3B30" stroke="#07111F" />}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3" data-testid="signal-feature-list">
        {run.peaks.map((p) => (
          <span key={p.label} className="flex items-center gap-1.5 font-mono text-[10px] text-pale/70">
            <span className="w-2 h-2 rounded-full bg-success inline-block" aria-hidden="true" /> {p.label} @ {p.t} min
          </span>
        ))}
        {run.artifact && (
          <span className="flex items-center gap-1.5 font-mono text-[10px] text-critical">
            <span className="w-2 h-2 rounded-full bg-critical inline-block" aria-hidden="true" /> {run.artifact.label} @ {run.artifact.t} min
          </span>
        )}
      </div>
    </ChartPanel>
  );
}
